import { sumWithDecimal, Decimal } from "@syla/shared/decimal";
import {
  satisfiesEntityType,
  satisfiesActivityType,
  satisfiesTransactionCount,
  satisfiesAllRequirements,
} from "@syla/shared/types/helpers/permissions";
import {
  getCurrentSubscription,
  PlanStatus,
} from "@syla/shared/types/helpers/subscriptions";
import { WithModelIds } from "@syla/shared/types/helpers/WithModelIds";
import { PlanBase } from "@syla/shared/types/models/PlanBase";
import { sortBy } from "lodash";
import { useCurrentAccountStore } from "../store/currentAccountStore";
import { useTransactionsStore } from "../store/transactionsStore";
import { useQueryGetAccount } from "../store/useQueryGetAccount";
import { useQueryGetPlans } from "../store/useQueryGetPlans";
import { useQueryGetTransactionCount } from "../store/useQueryGetTransactionCount";
import { useQueryModelTaxResult } from "../store/useQueryModelTaxResult";
import { useQuerySubscriptions } from "../store/useQuerySubscriptions";
import { useSelectedFinancialYear } from "../store/useSelectedFinancialYear";
import { getPlanStatus } from "./accountSettings/subscription/getPlanStatus";

type Plan = WithModelIds<PlanBase<any>>;

export type RecommendedReason =
  | "transactionCapExceeded"
  | "requiresEntity"
  | "requiresTradingStock"
  | "hasTaxSaving";

// determine recommended plan and other things
export const useGetAccountPlanProps = () => {
  const financialYear = useSelectedFinancialYear();

  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const isRecalculating = useTransactionsStore((state) => state.updating);

  const { data: account, isFetching } = useQueryGetAccount(accountId);

  const { data: transactionCountData, isLoading: isTransactionCountLoading } =
    useQueryGetTransactionCount(accountId);
  const hasTransactionCount = transactionCountData !== undefined;

  const { data: plansData, isLoading: isPlansLoading } = useQueryGetPlans();

  const { data: subscriptionsData, isLoading: subscriptionsLoading } =
    useQuerySubscriptions();

  const subscriptions = subscriptionsData?.subscriptions ?? [];
  let currentSubscription = getCurrentSubscription(subscriptions);
  const planStatus = getPlanStatus(currentSubscription);
  if (planStatus === PlanStatus.Expired) {
    currentSubscription = undefined;
  }

  const currentPlan = currentSubscription?.plan;

  const availablePlans = sortBy(
    (plansData?.plans ?? []).filter((plan) => {
      // always include the user's existing plan
      if (plan._id === currentPlan?._id) {
        return true;
      }

      // inactive plans are excluded as they are no longer available
      if (!plan.active) {
        return false;
      }

      // Exclude a newer version of the user's current plan. Relevant if
      // the user is currently on a legacy plan which only differs on price.
      if (plan.stripeProductId === currentPlan?.stripeProductId) {
        return false;
      }

      return true;
    }),
    (plan) => plan.price,
  );

  const suitablePlans = availablePlans.filter((plan) =>
    satisfiesAllRequirements({
      plan,
      account,
      transactionCount: transactionCountData,
    }),
  );

  const recommendations = suitablePlans.filter(
    (plan) =>
      // never suggest a cheaper plan
      (!currentPlan || plan.price > currentPlan.price) &&
      // always recommend the user at least gets assurance reports
      plan.features.assuranceReports,
  );

  let recommendedPlan: Plan | undefined = undefined;
  let recommendedReason: RecommendedReason | undefined = undefined;
  let runTaxSavingCalc = false;

  const comparisonPlan =
    currentPlan ?? (availablePlans.length > 0 ? availablePlans[0] : undefined);

  if (
    !recommendations.length ||
    !account ||
    isFetching ||
    !hasTransactionCount ||
    !comparisonPlan
  ) {
    recommendedPlan = undefined;
  }

  // recommend upgrade due to exeeding transaction count on an existing plan
  else if (
    currentPlan &&
    !satisfiesTransactionCount({
      plan: currentPlan,
      transactionCount: transactionCountData,
    })
  ) {
    recommendedReason = "transactionCapExceeded";
    recommendedPlan = recommendations[0];
  } else if (!satisfiesActivityType({ plan: comparisonPlan, account })) {
    recommendedReason = "requiresTradingStock";
    recommendedPlan = recommendations[0];
  }

  // tax optimisation calc is expensive, so we only run it when it's necessary
  else if (
    // no need to calc if the user already has tax optimisation
    !currentPlan?.features.taxOptimisation &&
    // no need to calc if there are no transactions
    transactionCountData > 0 &&
    // no need to calc if tax optimisation isn't a differentiating
    // feature
    recommendations.some((plan) => plan.features.taxOptimisation) &&
    suitablePlans.some((plan) => !plan.features.taxOptimisation)
  ) {
    runTaxSavingCalc = true;
  }

  // run tax saving calculations
  const { data: modelTaxResultResponse, isStale: modelTaxResultStale } =
    useQueryModelTaxResult({
      financialYearValue: financialYear,
      enabled: runTaxSavingCalc && !isRecalculating,
    });

  const totalGainDifference =
    modelTaxResultResponse &&
    sumWithDecimal(modelTaxResultResponse.taxResultSavings, "gain");

  // if there is a tax saving, then find the first recommendation with tax
  // optimisation enabled, provided there is a net benefit.
  if (runTaxSavingCalc && totalGainDifference) {
    const taxOptimisationPlan = recommendations.find(
      (plan) =>
        plan.features.taxOptimisation &&
        totalGainDifference.gt(
          getUpgradeCost({ currentPlan, possiblePlan: plan }),
        ),
    );

    if (taxOptimisationPlan) {
      recommendedPlan = taxOptimisationPlan;
      recommendedReason = "hasTaxSaving";
    }
  }

  // in the rare case that nothing was suitable (e.g. user has more transactions
  // than any plan supports), then no plan is recommended.
  if (!recommendedPlan) {
    recommendedPlan = recommendations[0] as Plan | undefined;

    // check if we had to recommend Assurance due to the entity type
    if (
      comparisonPlan &&
      !satisfiesEntityType({ plan: comparisonPlan, account })
    ) {
      recommendedReason = "requiresEntity";
    }

    // check if we had to recommend due to transaction count
    else if (
      comparisonPlan &&
      !satisfiesTransactionCount({
        plan: comparisonPlan,
        transactionCount: transactionCountData,
      })
    ) {
      recommendedReason = "transactionCapExceeded";
    }
  }

  const upgradeCost =
    recommendedPlan &&
    getUpgradeCost({ currentPlan, possiblePlan: recommendedPlan });

  const isLoading =
    isFetching ||
    isPlansLoading ||
    isTransactionCountLoading ||
    subscriptionsLoading ||
    (runTaxSavingCalc && (modelTaxResultStale || isRecalculating));

  return {
    currentPlan,
    currentSubscription,
    // all plans available / visible to the user, sorted by increasing price
    availablePlans,
    // plans which are actually viable for the user
    // (e.g. Budget is not suitable if the entity type is SMSF).
    // can be empty if the user has exceeded the transaction cap on all plans
    suitablePlans,
    // the plan we recommend the user to purchase or undefined if none
    recommendedPlan,
    recommendedReason,
    upgradeCost,
    totalGainDifference,
    yearlySavings: modelTaxResultResponse?.taxResultSavings,
    isLoading,
    transactionCount: transactionCountData,
    account,
  };
};

const getUpgradeCostCents = ({
  currentPlan,
  possiblePlan,
}: {
  currentPlan: Plan | undefined;
  possiblePlan: Plan;
}) => possiblePlan.price - (currentPlan?.price ?? 0);

const getUpgradeCost = (props: {
  currentPlan: Plan | undefined;
  possiblePlan: Plan;
}) => Decimal.from(getUpgradeCostCents(props).toString()).div("100");
